import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  HeartHandshake,
  ShieldCheck,
} from "lucide-react";
import Button from "../components/Button";
import { Blob, Dots, Star } from "../components/Decor";
import AccentRays from "../components/decor/AccentRays";
import HandDrawnUnderline from "../components/decor/HandDrawnUnderline";
import brandLogo from "../assets/images/logo-fix.png";
import SEO from "../components/SEO";

const roles = {
  school: {
    title: "Pendaftaran Sekolah",
    description:
      "Anda telah memilih peran Perwakilan Sekolah. Formulir pendaftaran sekolah belum tersedia pada versi ini.",
    icon: GraduationCap,
    accentClass: "bg-purple/10 text-purple",
  },
  parent: {
    title: "Pendaftaran Orang Tua",
    description:
      "Anda telah memilih peran Orang Tua / Wali Murid. Formulir pendaftaran orang tua belum tersedia pada versi ini.",
    icon: HeartHandshake,
    accentClass: "bg-yellow/35 text-ink",
  },
};

function PageDecor() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <Dots className="absolute left-4 top-10 h-20 w-20 opacity-35 sm:left-10" color="#73DCC6" />
      <Star className="absolute right-[8%] top-16 h-7 w-7 opacity-75" color="#6554E8" />
      <Star className="absolute left-[11%] top-[42%] hidden h-4 w-4 opacity-55 lg:block" color="#FFD966" />
      <Blob className="absolute -bottom-28 -left-24 h-72 w-72 opacity-20" color="#BEB5FF" />
      <svg
        viewBox="0 0 170 70"
        className="absolute -bottom-1 right-3 h-20 w-44 opacity-70 sm:right-10"
        fill="none"
        focusable="false"
      >
        <path
          d="M5 55c28-3 33-32 50-31 11 .5 5 22-6 20-12-2-3-27 13-24 14 3 10 28-2 31-9 2-13-17 2-22 23-8 30 29 56 13 12-7 20-18 26-31"
          stroke="#E7B928"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function RoleDestination({ role }) {
  const { title, description, icon: Icon, accentClass } = roles[role];

  return (
    <main className="relative min-h-[78vh] overflow-hidden bg-warm px-4 py-12 sm:py-16">
      <PageDecor />
      <div className="relative z-10 mx-auto flex max-w-xl flex-col items-center text-center">
        <Link to="/" aria-label="Kembali ke beranda MySchoolMate" className="rounded-lg focus-ring">
          <img
            src={brandLogo}
            alt="MySchoolMate"
            width="288"
            height="43"
            className="h-8 w-auto sm:h-9"
          />
        </Link>

        <section className="mt-10 w-full rounded-[28px] border-2 border-ink/15 bg-white p-7 shadow-card sm:p-10">
          <span className={`mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] ${accentClass}`}>
            <Icon className="h-10 w-10" aria-hidden="true" />
          </span>
          <h1 className="mt-6 text-3xl font-extrabold text-ink sm:text-4xl">{title}</h1>
          <p className="mx-auto mt-4 max-w-md font-medium leading-relaxed text-ink/70">{description}</p>
          <Button to="/signup" variant="secondary" className="mt-8 min-h-12 w-full sm:w-auto">
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            Pilih peran lain
          </Button>
        </section>

        <p className="mt-7 text-sm font-medium text-ink/65">
          Sudah punya akun?{" "}
          <Link to="/login" className="rounded-sm font-bold text-purple underline-offset-4 hover:underline focus-ring">
            Masuk di sini
          </Link>
        </p>
      </div>
    </main>
  );
}

export default function Signup() {
  const [searchParams] = useSearchParams();
  const selectedRole = searchParams.get("role");

  if (roles[selectedRole]) {
    return <RoleDestination role={selectedRole} />;
  }

  return (
    <main className="relative flex-1 flex flex-col overflow-hidden bg-cream px-4 py-10 sm:px-6 sm:py-14 lg:py-16">
      <SEO title="Daftar | MySchoolMate" canonicalPath="/signup" noIndex />
      <PageDecor />

      <div className="relative z-10 mx-auto max-w-[960px]">
        <header className="mx-auto max-w-2xl text-center">
          <Link to="/" aria-label="Kembali ke beranda MySchoolMate" className="inline-flex rounded-lg focus-ring">
            <img
              src={brandLogo}
              alt="MySchoolMate"
              width="288"
              height="43"
              className="h-8 w-auto sm:h-10"
            />
          </Link>

          <div className="relative mt-8 sm:mt-10">
            <AccentRays
              variant="curved-rays"
              color="mint"
              rotation={18}
              className="absolute -right-1 -top-8 hidden h-10 w-10 sm:block md:right-12"
            />
            <h1 className="text-[2rem] font-extrabold leading-[1.16] text-ink sm:text-[2.5rem] lg:text-[2.75rem]">
              Mulai perjalanan bersama{" "}
              <span className="relative inline-block whitespace-nowrap">
                MySchoolMate
                <HandDrawnUnderline
                  variant="swoosh"
                  color="mint"
                  className="absolute -bottom-3 left-0 h-4 w-full"
                />
              </span>
            </h1>
          </div>

          <p className="mx-auto mt-6 max-w-[610px] text-[15px] font-medium leading-relaxed text-ink/65 sm:text-base">
            Pilih peran Anda untuk melanjutkan pendaftaran dan mendapatkan pengalaman yang sesuai.
          </p>
        </header>

        <section className="mt-10 grid gap-5 md:grid-cols-2 md:gap-6 lg:mt-12" aria-label="Pilihan peran">
          <article className="group flex min-h-[480px] flex-col items-center rounded-[28px] border-2 border-purple/25 bg-[#F8F6FF] p-6 text-center shadow-card transition duration-200 md:hover:-translate-y-1 md:hover:border-purple/45 md:hover:shadow-card-hover sm:p-8">
            <div className="relative flex h-40 w-full items-center justify-center overflow-hidden rounded-[24px] bg-gradient-to-br from-[#EEE9FF] to-white sm:h-44">
              <div className="absolute h-32 w-32 rounded-full bg-purple/10 sm:h-36 sm:w-36" />
              <img
                src="/assets/kepala-sekolah.png"
                alt="Ilustrasi kepala sekolah"
                width="760"
                height="475"
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-purple/20 bg-white text-purple shadow-sm">
                <GraduationCap className="h-5 w-5" aria-hidden="true" />
              </span>
            </div>

            <h2 className="mt-7 text-2xl font-extrabold text-ink">Perwakilan Sekolah</h2>
            <span className="mt-3 h-1 w-10 rounded-full bg-purple" aria-hidden="true" />
            <p className="mt-4 max-w-sm font-medium leading-relaxed text-ink/70">
              Daftarkan sekolah dan kelola informasi profil agar lebih mudah ditemukan oleh keluarga.
            </p>
            <Button
              to="/daftar/sekolah"
              variant="secondary"
              className="mt-auto min-h-12 w-full border-purple bg-white/75 text-purple hover:bg-purple hover:text-white"
            >
              Daftar sebagai Sekolah
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Button>
          </article>

          <article className="group flex min-h-[480px] flex-col items-center rounded-[28px] border-2 border-[#E7B928]/35 bg-[#FFF9E8] p-6 text-center shadow-card transition duration-200 md:hover:-translate-y-1 md:hover:border-[#E7B928]/65 md:hover:shadow-card-hover sm:p-8">
            <div className="relative h-40 w-full overflow-hidden rounded-[24px] bg-[#FFF1BE] sm:h-44">
              <img
                src="/assets/landing/school-choice-parent-child.webp"
                alt="Orang tua dan anak melihat informasi sekolah bersama"
                width="1024"
                height="768"
                className="h-full w-full object-cover object-[center_42%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-yellow/15 via-transparent to-white/5" aria-hidden="true" />
              <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#E7B928]/30 bg-white text-ink shadow-sm">
                <HeartHandshake className="h-5 w-5" aria-hidden="true" />
              </span>
            </div>

            <h2 className="mt-7 text-2xl font-extrabold text-ink">Orang Tua / Wali Murid</h2>
            <span className="mt-3 h-1 w-10 rounded-full bg-[#E7B928]" aria-hidden="true" />
            <p className="mt-4 max-w-sm font-medium leading-relaxed text-ink/70">
              Cari, bandingkan, dan temukan pilihan sekolah yang sesuai dengan kebutuhan anak dan keluarga.
            </p>
            <Button to="/daftar/orang-tua" variant="yellow" className="mt-auto min-h-12 w-full">
              Daftar sebagai Orang Tua
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Button>
          </article>
        </section>

        <div className="mx-auto mt-7 flex max-w-[720px] items-start justify-center gap-3 rounded-[18px] border border-ink/10 bg-white/80 px-5 py-4 text-left shadow-sm sm:items-center sm:rounded-full sm:text-center">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-mint sm:mt-0" aria-hidden="true" />
          <p className="text-sm font-medium leading-relaxed text-ink/65">
            Informasi akun digunakan untuk mendukung pengalaman Anda di MySchoolMate.
          </p>
        </div>

        <p className="mt-6 text-center text-sm font-medium text-ink/65">
          Sudah punya akun?{" "}
          <Link to="/login" className="rounded-sm font-bold text-purple underline-offset-4 hover:underline focus-ring">
            Masuk di sini
          </Link>
        </p>
      </div>
    </main>
  );
}
